// =============================================================================
// PROOF BUCKET COMPUTATION FROM CACHED DAILY ROWS
// =============================================================================
// PURPOSE: Compute bucket counts for proof modals from cached setter_daily_performance
// arrays — instant, zero network, no 30s RPC.
//
// WHY: The daily table stores individual value arrays (avg_reply_time_values,
// engaged_response_values, etc.) that can be bucket-counted client-side.
// This gives instant bucket bars on modal open instead of waiting for the
// 30s proof RPC.
//
// WHAT THIS CANNOT DO:
// - "suffering" bucket for avg_reply_time — this is date-range-parameterized
//   (depends on cross-day session chains). Falls back to RPC.
// - Individual conversation rows — those still come from get_setter_proof RPC.
//
// ARCHITECTURE:
// Phase 1: This function → instant bucket bars
// Phase 2: get_setter_proof RPC → conversation rows (background, may be slow)
// =============================================================================

import type { DailyRow } from "./aggregateDailyRows";
import type { ProofMetricType } from "../types/core";

export interface CachedBucketCount {
  bucket: string;
  count: number;
}

export interface CachedSetterBuckets {
  setterId: string;
  setterName: string;
  buckets: CachedBucketCount[];
  totalCount: number;
  /** True if some buckets couldn't be computed from cache (e.g. suffering) */
  hasPartialData: boolean;
}

/** Metrics where we can compute meaningful bucket counts from cached daily rows.
 * ONLY include metrics that have BOTH:
 * 1. A real switch case in computeProofBuckets() that produces non-empty buckets
 * 2. Matching branches in get_setter_proof_rows.sql for per-bucket drill-down
 *
 * followup_persistence and conversation_span were removed — they're cross-day metrics
 * that cannot be computed from daily rows. Their computeProofBuckets switch case doesn't
 * exist, so they hit the default (empty buckets), causing usePaginatedProof=true with
 * zero bars and no fallback to get_setter_proof. See handoff Mar 22 audit. */
const CACHEABLE_METRICS: Set<ProofMetricType> = new Set([
  "avg_reply_time",
  "engaged_response",
  "lead_reply_time",
  // reply_turn_distribution is NOT cacheable from daily rows — it uses live
  // get_reply_turn_distribution RPC with lifetime c.reply_turns to avoid
  // double-counting conversations active across multiple days.
  "ignored_per_session",
]);

export function canComputeBucketsFromCache(metricType: ProofMetricType): boolean {
  return CACHEABLE_METRICS.has(metricType);
}

/** Parse Postgres NUMERIC[] that might come as string[] from PostgREST */
function numArray(v: unknown): number[] {
  if (!v || !Array.isArray(v)) return [];
  return v.map((x) => (typeof x === "number" ? x : parseFloat(x as string) || 0));
}

function num(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  return parseFloat(v as string) || 0;
}

/**
 * Compute proof bucket counts from cached daily rows.
 * Returns per-setter bucket counts that can render BucketSummary instantly.
 */
export function computeProofBuckets(
  dailyRows: DailyRow[],
  startDate: string,
  endDate: string,
  metricType: ProofMetricType,
  setterNames: Record<string, string>,
  setterIds?: string[]
): CachedSetterBuckets[] {
  // Filter to date range and optionally to specific setters
  const filtered = dailyRows.filter(
    (r) =>
      r.work_date >= startDate &&
      r.work_date <= endDate &&
      (!setterIds || setterIds.length === 0 || setterIds.includes(r.setter_id))
  );

  // Group by setter
  const bySetterId = new Map<string, DailyRow[]>();
  for (const row of filtered) {
    const existing = bySetterId.get(row.setter_id) || [];
    existing.push(row);
    bySetterId.set(row.setter_id, existing);
  }

  const result: CachedSetterBuckets[] = [];

  for (const [setterId, rows] of bySetterId) {
    const setterName = setterNames[setterId] || setterId;
    let buckets: CachedBucketCount[];
    let totalCount: number;
    let hasPartialData = false;

    switch (metricType) {
      case "avg_reply_time": {
        // Values are individual reply times in minutes (excludes suffering)
        const values = rows.flatMap((r) => numArray(r.avg_reply_time_values));
        const lt30 = values.filter((v) => v < 30).length;
        const lt120 = values.filter((v) => v >= 30 && v < 120).length;
        const gte120 = values.filter((v) => v >= 120).length;
        const ignored = rows.reduce((s, r) => s + num(r.total_ignored_conversations), 0);

        buckets = [
          { bucket: "<30min", count: lt30 },
          { bucket: "30min-2hr", count: lt120 },
          { bucket: "2hr+", count: gte120 },
          { bucket: "ignored", count: ignored },
        ];
        totalCount = lt30 + lt120 + gte120 + ignored;
        // Note: "suffering" bucket excluded — requires cross-day session analysis
        // that can't be computed from daily rows. Will be backfilled lazily via RPC.
        break;
      }

      case "engaged_response": {
        const values = rows.flatMap((r) => numArray(r.engaged_response_values));
        const lt5 = values.filter((v) => v < 5).length;
        const lt15 = values.filter((v) => v >= 5 && v < 15).length;
        const gte15 = values.filter((v) => v >= 15).length;

        buckets = [
          { bucket: "<5min", count: lt5 },
          { bucket: "5-15min", count: lt15 },
          { bucket: "15min+", count: gte15 },
        ];
        totalCount = values.length;
        break;
      }

      case "lead_reply_time": {
        const values = rows.flatMap((r) => numArray(r.lead_reply_values));
        const lt5 = values.filter((v) => v < 5).length;
        const lt30 = values.filter((v) => v >= 5 && v < 30).length;
        const lt120 = values.filter((v) => v >= 30 && v < 120).length;
        const gte120 = values.filter((v) => v >= 120).length;

        buckets = [
          { bucket: "<5min", count: lt5 },
          { bucket: "5-30min", count: lt30 },
          { bucket: "30min-2hr", count: lt120 },
          { bucket: "2hr+", count: gte120 },
        ];
        totalCount = values.length;
        break;
      }

      case "reply_turn_distribution": {
        buckets = [
          { bucket: "deep", count: rows.reduce((s, r) => s + num(r.reply_turn_deep), 0) },
          { bucket: "engaged", count: rows.reduce((s, r) => s + num(r.reply_turn_engaged), 0) },
          { bucket: "light", count: rows.reduce((s, r) => s + num(r.reply_turn_light), 0) },
          {
            bucket: "outbound_no_reply",
            count: rows.reduce((s, r) => s + num(r.reply_turn_outbound_no_reply), 0),
          },
          { bucket: "ghost", count: rows.reduce((s, r) => s + num(r.reply_turn_ghost), 0) },
        ];
        totalCount = buckets.reduce((s, b) => s + b.count, 0);
        break;
      }

      case "ignored_per_session": {
        // Daily rows have total_ignored_conversations + session_count but no per-session
        // breakdown. We can only surface the aggregate total — drill-down via paginated RPC
        // will show the per-session detail (session_start, session_end, conversation_id).
        const totalIgnored = rows.reduce((s, r) => s + num(r.total_ignored_conversations), 0);
        const totalSessions = rows.reduce((s, r) => s + num(r.session_count), 0);

        buckets = [{ bucket: "all", count: totalIgnored }];
        totalCount = totalIgnored;
        // hasPartialData = true: we show total ignored count but can't bucket by
        // "0 per session / 1-2 / 3+" without per-session data in the daily table
        hasPartialData = true;

        // Surface session count as context (shown in modal header but not as a bucket bar)
        void totalSessions; // referenced above for documentation purposes
        break;
      }

      // followup_persistence and conversation_span are cross-day metrics
      // They default to 0 in client-side aggregation, so we can't compute from cache
      default:
        buckets = [];
        totalCount = 0;
        hasPartialData = true;
        break;
    }

    result.push({ setterId, setterName, buckets, totalCount, hasPartialData });
  }

  return result;
}
